import React, { useEffect } from 'react';

// Shared inline-styles kept small and reused across the page. Extracted to
// module scope so re-renders don't re-create the objects.
const STYLES = {
  page: {
    fontFamily: '-apple-system, Segoe UI, Roboto, Helvetica, sans-serif',
    color: '#1b2a22',
    maxWidth: 900,
    margin: '0 auto',
    padding: '32px 20px',
    lineHeight: 1.6,
    background: '#fff',
    minHeight: '100vh',
  },
  h1: { color: '#1B3B2B', fontSize: 26, marginBottom: 8 },
  h2: { color: '#1B3B2B', fontSize: 18, marginTop: 28 },
  muted: { color: '#5b6b62', fontSize: 14 },
  callout: {
    background: '#eef3ef',
    borderLeft: '4px solid #1B3B2B',
    padding: '12px 16px',
    borderRadius: 8,
    margin: '16px 0',
  },
  ol: { paddingLeft: 22 },
  ul: { paddingLeft: 22 },
  shots: { display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 16 },
  shot: { flex: 1, minWidth: 260, textAlign: 'center' },
  shotImg: { width: '100%', maxWidth: 320, border: '1px solid #d5e0d9', borderRadius: 14 },
  shotSpan: { display: 'block', marginTop: 8, fontSize: 13, color: '#5b6b62' },
  code: { background: '#eef3ef', padding: '1px 6px', borderRadius: 5 },
  a: { color: '#0a6b3d' },
};

function usePageMeta(title) {
  useEffect(() => {
    document.title = title;
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex,nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, [title]);
}

function Header() {
  return (
    <>
      <h1 style={STYLES.h1}>LISTING AiGENT — SMS Opt-In Proof</h1>
      <p style={STYLES.muted}>
        For A2P 10DLC / Call-to-Action (CTA) campaign verification &middot; Last updated July 2026
      </p>
      <p>
        <strong>Brand:</strong> LISTING AiGENT &nbsp;|&nbsp;
        <strong>Website:</strong>{' '}
        <a href="https://listingaigent.com" style={STYLES.a}>https://listingaigent.com</a> &nbsp;|&nbsp;
        <strong>Program:</strong> Account verification (2FA) for real-estate agents who create an account in the LISTING AiGENT mobile app.
      </p>
      <div style={STYLES.callout}>
        <strong>Note for reviewers:</strong> LISTING AiGENT is a native mobile app (iOS/Android). The sign-up / opt-in form is inside the app. This public page hosts screenshots of the exact opt-in flow and the consent checkbox so it can be reviewed without installing the app.
      </div>
    </>
  );
}

function SignupLocation() {
  return (
    <>
      <h2 style={STYLES.h2}>Where users sign up &amp; opt in</h2>
      <p>
        Users create an account on the <strong>Sign Up</strong> screen of the LISTING AiGENT app.
        The opt-in is a <strong>required, unchecked-by-default checkbox</strong> directly below the mobile number field.
      </p>
    </>
  );
}

const OPTIN_STEPS = [
  'User opens the LISTING AiGENT app and taps Sign Up.',
  'User enters Full name, Email, Password, and Mobile number.',
  'User must actively check the SMS consent checkbox (it is not pre-checked).',
  'User taps Create Account. The account is not created unless the box is checked.',
  'The consent (value, timestamp, and exact language) is stored with the user\u2019s record.',
];

function OptinFlow() {
  return (
    <>
      <h2 style={STYLES.h2}>Opt-in flow (step by step)</h2>
      <ol style={STYLES.ol}>
        <li>User opens the LISTING AiGENT app and taps <strong>Sign Up</strong>.</li>
        <li>User enters Full name, Email, Password, and <strong>Mobile number</strong>.</li>
        <li>User must actively check the SMS consent checkbox (it is <em>not</em> pre-checked).</li>
        <li>User taps <strong>Create Account</strong>. The account is not created unless the box is checked.</li>
        <li>The consent (value, timestamp, and exact language) is stored with the user&apos;s record.</li>
      </ol>
    </>
  );
}

function ConsentLanguage() {
  return (
    <>
      <h2 style={STYLES.h2}>Exact consent language shown at sign-up</h2>
      <div style={STYLES.callout}>
        &quot;I agree to receive text messages (SMS) from LISTING AiGENT, including security verification codes, listing details,
        and appointment reminders, at the number I provide. Msg &amp; data rates may apply. Msg frequency varies. Reply <code style={STYLES.code}>STOP</code> to unsubscribe,
        <code style={STYLES.code}> HELP</code> for help. View SMS Policy. View Privacy Policy.&quot;
      </div>
    </>
  );
}

function ScreenshotCard({ src, alt, caption }) {
  return (
    <div style={STYLES.shot}>
      <img src={src} alt={alt} style={STYLES.shotImg} />
      <span style={STYLES.shotSpan}>{caption}</span>
    </div>
  );
}

function ScreenshotsSection() {
  return (
    <>
      <h2 style={STYLES.h2}>Screenshots of the opt-in flow</h2>
      <div style={STYLES.shots}>
        <ScreenshotCard
          src="/legal/optin-unchecked.webp"
          alt="Sign-up form with unchecked consent box"
          caption="1. Consent box unchecked (default) — the box is not pre-selected."
        />
        <ScreenshotCard
          src="/legal/optin-checked.webp"
          alt="Sign-up form with checked consent box"
          caption="2. User checks the box to give explicit SMS consent, then taps Create Account."
        />
      </div>
    </>
  );
}

function SampleMessages() {
  return (
    <>
      <h2 style={STYLES.h2}>Message types &amp; sample messages</h2>
      <ul style={STYLES.ul}>
        <li>Verification (2FA): <em>&quot;Your LISTING AiGENT verification code is 123456. Reply STOP to opt out, HELP for help.&quot;</em></li>
      </ul>
      <p>
        <strong>Message frequency:</strong> varies with account activity. <strong>Costs:</strong> Msg &amp; data rates may apply.{' '}
        <strong>Opt out:</strong> reply STOP. <strong>Help:</strong> reply HELP or email listingaigent@gmail.com.
      </p>
      <p>
        Full policy: <a href="/terms#s8" style={STYLES.a}>SMS / Messaging Policy (Terms &sect;8)</a>{' '}·{' '}
        <a href="/privacy" style={STYLES.a}>Privacy Policy</a>.
      </p>
    </>
  );
}

export default function OptinProof() {
  usePageMeta('LISTING AiGENT \u2014 SMS Opt-In Proof (A2P 10DLC)');

  return (
    <div style={STYLES.page}>
      <Header />
      <SignupLocation />
      <OptinFlow />
      <ConsentLanguage />
      <ScreenshotsSection />
      <SampleMessages />
    </div>
  );
}

export { OPTIN_STEPS };
